import {
  z,
  ZodTypeAny,
  ZodObject,
  ZodArray,
  ZodUnion,
  ZodLazy,
} from 'zod'

export const ZOD_META = Symbol('zod.meta')

export type ZodMeta = {
  [key: string]: unknown
}

/**
 * Métodos adicionais que queremos injetar no schema.
 */
export interface ZodTypeWithMeta {
  getMeta(): ZodMeta
}

/**
 * Tipo para envio de shape via API
 */
export type ZodSchemaInfo = {
  type: string
  meta: ZodMeta
  label?: string
  shape?: ZodSchemaInfo | Record<string, ZodSchemaInfo> | ZodSchemaInfo[]
}

/**
 * Função para obter metadados de um schema Zod estendido com withMeta
 * @param schema Schema Zod que pode conter metadados
 * @returns Objeto com os metadados ou um objeto vazio se não houver metadados
 */
export function getMeta<T extends z.ZodTypeAny>(schema: T): ZodMeta {
  // Verifica se é um schema estendido com withMeta (tem o método getMeta)
  if (typeof (schema as any).getMeta === 'function') {
    return (schema as any).getMeta()
  }

  // Se não tiver o método getMeta, tenta acessar diretamente a propriedade
  return ((schema as any)._def?.[ZOD_META] as ZodMeta) || undefined
}

/**
 * Aqui preservamos o schema original e só adicionamos os métodos extras.
 */
export function withMeta<T extends z.ZodTypeAny>(schema: T): T & ZodTypeWithMeta {
  const extendedSchema = schema as T & ZodTypeWithMeta

  ;(extendedSchema as any).meta = function (metadata?: ZodMeta) {
    if (metadata === undefined) {
      return ((this._def as any)[ZOD_META] as ZodMeta) || undefined
    }

    ;(this._def as any)[ZOD_META] = metadata
    return this
  }

  extendedSchema.getMeta = function () {
    return ((this._def as any)[ZOD_META] || {}) as ZodMeta
  }

  return extendedSchema
}

/**
 * Exemplo de wrappers para tipos primitivos:
 */
export const string = () => withMeta(z.string())
export const number = () => withMeta(z.number())
export const boolean = () => withMeta(z.boolean())
export const date = () => withMeta(z.date())
export const dateTime = () => withMeta(z.date())

function getSchemaTypeName(schema: ZodTypeAny): string {
  return (schema as any)?._def?.typeName ?? (schema as any)?._def?.type ?? (schema as any)?.def?.type ?? ''
}

function describeSchema(schema: ZodTypeAny, description?: string): ZodTypeAny {
  if (!description) return schema
  return schema.describe(description)
}

function getPipeInnerSchema(schema: ZodTypeAny): ZodTypeAny | undefined {
  const inputSchema = (schema as any).in as ZodTypeAny | undefined
  const outputSchema = (schema as any).out as ZodTypeAny | undefined

  if (!inputSchema) return outputSchema
  if (!outputSchema) return inputSchema

  const inputSchemaTypeName = getSchemaTypeName(inputSchema)
  const outputSchemaTypeName = getSchemaTypeName(outputSchema)

  if (inputSchemaTypeName === 'transform' || inputSchemaTypeName === 'ZodTransform') {
    return outputSchema
  }

  if (outputSchemaTypeName === 'transform' || outputSchemaTypeName === 'ZodTransform') {
    return inputSchema
  }

  return outputSchema
}

function getWrappedSchema(schema: ZodTypeAny): ZodTypeAny | undefined {
  const schemaTypeName = getSchemaTypeName(schema)

  if (schemaTypeName === 'ZodEffects') {
    return (schema as any).innerType?.() ?? (schema as any)?._def?.schema
  }

  if (
    schemaTypeName === 'ZodOptional' ||
    schemaTypeName === 'ZodNullable' ||
    schemaTypeName === 'ZodDefault' ||
    schemaTypeName === 'ZodCatch' ||
    schemaTypeName === 'ZodReadonly' ||
    schemaTypeName === 'ZodNonOptional' ||
    schemaTypeName === 'optional' ||
    schemaTypeName === 'nullable' ||
    schemaTypeName === 'default' ||
    schemaTypeName === 'catch' ||
    schemaTypeName === 'readonly' ||
    schemaTypeName === 'nonoptional' ||
    schemaTypeName === 'prefault' ||
    schemaTypeName === 'success'
  ) {
    return (
      (schema as any).removeDefault?.() ??
      (schema as any).removeCatch?.() ??
      (schema as any).unwrap?.() ??
      (schema as any)?._def?.innerType
    )
  }

  if (schemaTypeName === 'pipe') {
    return getPipeInnerSchema(schema)
  }

  return undefined
}

function getLazySchema(schema: ZodLazy): ZodTypeAny {
  return ((schema as any).unwrap?.() ?? (schema as any).schema) as ZodTypeAny
}

/**
 * Transforma zod schema em schema info para envio via API substituindo o antigo shape de variable
 * @param schema zod schema
 * @returns schema info
 */
export function getZodSchemaInfo(schema: ZodTypeAny): ZodSchemaInfo {
  const wrappedSchema = getWrappedSchema(schema)
  if (wrappedSchema) {
    return getZodSchemaInfo(describeSchema(wrappedSchema, schema.description))
  }

  const baseInfo: ZodSchemaInfo = {
    type: zodTypeToTsType(getSchemaTypeName(schema)),
    meta: getMeta(schema),
    label: schema.description,
  }

  if (schema instanceof ZodObject) {
    const shape = schema.shape
    const result: Record<string, ZodSchemaInfo> = {}

    Object.entries(shape).forEach(([key, value]) => {
      const info = getZodSchemaInfo(value as ZodTypeAny)
      result[key] = ('type' in info ? info : { type: 'object', shape: info }) as ZodSchemaInfo
    })

    baseInfo.shape = result
  }

  if (schema instanceof ZodArray) {
    const elementInfo = getZodSchemaInfo(schema.element as ZodTypeAny)
    baseInfo.shape = ('type' in elementInfo ? elementInfo : []) as ZodSchemaInfo
  } else if (schema instanceof ZodUnion) {
    if (schema.options.length > 0) {
      const schemaUnion = (schema.options?.[0] as ZodTypeAny)?.describe(schema.description || '')
      return getZodSchemaInfo(schemaUnion)
    } else return baseInfo
  } else if (((schema as any)?._type ?? (schema as any)?.type) === 'expression') {
    baseInfo.type = 'expression'
    if (baseInfo.meta) baseInfo.shape = getZodSchemaInfo(z.object({ ...(baseInfo.meta as any) }))
  } else if (schema instanceof ZodLazy) {
    baseInfo.shape = getZodSchemaInfo(getLazySchema(schema))
  }

  return baseInfo as ZodSchemaInfo
}

function zodTypeToTsType(zodTypeName: string): string {
  const typeMap: Record<string, string> = {
    ZodString: 'string',
    string: 'string',
    ZodNumber: 'number',
    number: 'number',
    ZodBoolean: 'boolean',
    boolean: 'boolean',
    ZodBigInt: 'bigint',
    bigint: 'bigint',
    ZodSymbol: 'symbol',
    symbol: 'symbol',
    ZodUndefined: 'undefined',
    undefined: 'undefined',
    ZodNull: 'null',
    null: 'null',
    ZodVoid: 'void',
    void: 'void',
    ZodAny: 'any',
    any: 'any',
    ZodUnknown: 'unknown',
    unknown: 'unknown',
    ZodNever: 'never',
    never: 'never',
    ZodDate: 'Date',
    date: 'Date',
    ZodArray: 'Array',
    array: 'Array',
    ZodObject: 'object',
    object: 'object',
    ZodRecord: 'Record<string, any>',
    record: 'Record<string, any>',
    ZodMap: 'Map',
    map: 'Map',
    ZodSet: 'Set',
    set: 'Set',
    ZodPromise: 'Promise',
    promise: 'Promise',
    ZodLiteral: 'literal',
    literal: 'literal',
    ZodEnum: 'enumerated',
    enum: 'enumerated',
    ZodNativeEnum: 'enum',
    ZodLazy: 'lazy',
    lazy: 'lazy',
    tuple: 'Array',
    template_literal: 'string',
  }
  return typeMap[zodTypeName] || 'unknown'
}

export function getBaseType(schema: z.ZodTypeAny): z.ZodTypeAny {
  const wrappedSchema = getWrappedSchema(schema)
  if (wrappedSchema) {
    return getBaseType(wrappedSchema)
  }

  return schema
}
